import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import {
  storyInit,
  getSession,
  searchGeneratedContent,
  generateStory,
  searchSession,
  changePreview,
  reGenerateStoryContent,
  setStoryContent,
  sendStory,
  searchMediaPool,
  getUsedMedia,
  clearGeneratedContent,
} from './storyActions';
import { MEDIA_POOL_SOURCE_MODES } from '../../activ8/story/adjust/media-pool/constants';

const DESCRIPTION_NOT_READY_STATUS = 417;

const isDescriptionNotReady = (error) => error?.response?.status === DESCRIPTION_NOT_READY_STATUS;

const initialState = {
  sessionId: '',
  isSessionLoading: false,

  selectedMedia: [],
  selectedMediaActiveId: null,
  selectedMessage: '',

  mediaPool: [],
  isMediaPoolLoading: false,
  isMediaPoolDescriptionPending: false,

  usedMedia: [],
  isUsedMediaLoading: false,

  mediaPoolSourceMode: MEDIA_POOL_SOURCE_MODES.MEDIA,
  mediaPoolSearchQuery: '',

  generatedContent: [],
  generatedContentTableSettings: {
    page: 0,
    size: 10,
    sort: '-createdOn',
    totalElements: 0,
  },

  sessionsData: [],
  isSessionsLoading: false,
  sessionsTableSettings: {
    page: 0,
    size: 10,
    sort: '-createdOn',
    totalElements: 0,
    username: '',
  },

  isStorySending: false,
  isGeneratedContentClearing: false,
};

const storySlice = createSlice({
  name: 'story',
  initialState,
  reducers: {
    setSelectedMedia: (state, action) => {
      state.selectedMedia = action.payload;
    },
    setSelectedMediaActiveId: (state, action) => {
      state.selectedMediaActiveId = action.payload;
    },
    setMediaPoolSourceMode: (state, action) => {
      state.mediaPoolSourceMode = action.payload;
    },
    setMediaPoolSearchQuery: (state, action) => {
      state.mediaPoolSearchQuery = action.payload;
    },
    setUsedMedia: (state, action) => {
      state.usedMedia = action.payload;
    },
    setMediaPoolDescriptionPending: (state, action) => {
      state.isMediaPoolDescriptionPending = action.payload;
    },
    clearMediaPool: (state) => {
      state.mediaPool = [];
      state.isMediaPoolLoading = false;
      state.isMediaPoolDescriptionPending = false;
    },
    setSessionId: (state, action) => {
      state.sessionId = action.payload;
      state.selectedMedia = [];
      state.selectedMediaActiveId = null;
      state.mediaData = [];
      state.generatedContent = [];
      state.mediaPool = [];
      state.isMediaPoolLoading = false;
      state.isMediaPoolDescriptionPending = false;
      state.mediaPoolSearchQuery = '';
    },
    selectSessionFromHistory: (state, action) => {
      const { id, message, usedMedia } = action.payload;

      state.sessionId = id;
      state.selectedMessage = message ?? '';
      state.selectedMedia = [];
      state.selectedMediaActiveId = null;
      state.mediaData = [];
      state.generatedContent = [];
      state.mediaPool = [];
      state.isMediaPoolLoading = false;
      state.isMediaPoolDescriptionPending = false;
      state.mediaPoolSearchQuery = '';
      state.usedMedia = usedMedia ?? [];
    },
    changeSessionsTableSettings: (state, action) => {
      state.sessionsTableSettings = { ...state.sessionsTableSettings, ...action.payload };
    },
    changeGeneratedContentTableSettings: (state, action) => {
      state.generatedContentTableSettings = { ...state.generatedContentTableSettings, ...action.payload };
    },
    changeSelectedMessage: (state, action) => {
      state.selectedMessage = action.payload;
    },
    changeIsSessionLoading: (state, action) => {
      state.isSessionLoading = action.payload;
    },

    clearBeforeInit: (state) => {
      state.selectedMedia = initialState.selectedMedia;
      state.selectedMediaActiveId = initialState.selectedMediaActiveId;
      state.selectedMessage = initialState.selectedMessage;

      state.mediaPool = initialState.mediaPool;
      state.isMediaPoolLoading = initialState.isMediaPoolLoading;
      state.isMediaPoolDescriptionPending = initialState.isMediaPoolDescriptionPending;
      state.usedMedia = initialState.usedMedia;
      state.mediaPoolSourceMode = initialState.mediaPoolSourceMode;
      state.mediaPoolSearchQuery = initialState.mediaPoolSearchQuery;

      state.mediaData = initialState.mediaData;
      state.generatedContent = initialState.generatedContent;
      state.mediaGenerated = initialState.mediaGenerated;
      state.generatedContentTableSettings = initialState.generatedContentTableSettings;
      state.sessionsData = initialState.sessionsData;
      state.sessionsTableSettings = initialState.sessionsTableSettings;
      state.sessionId = initialState.sessionId;
    },
    resetStory: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(isAnyOf(storyInit.fulfilled, getSession.fulfilled), (state, action) => {
        state.sessionId = action.payload.sessionId;
        state.isSessionLoading = false;
      })
      .addMatcher(isAnyOf(storyInit.pending), (state) => {
        state.isSessionLoading = true;
      })
      .addMatcher(isAnyOf(getSession.pending), (state) => {
        state.isSessionLoading = true;
        state.selectedMedia = [];
        state.selectedMediaActiveId = null;
        state.mediaData = [];
        state.generatedContent = [];
        state.mediaPool = [];
        state.isMediaPoolLoading = false;
        state.isMediaPoolDescriptionPending = false;
      })
      .addMatcher(isAnyOf(storyInit.rejected, getSession.rejected), (state) => {
        state.sessionId = '';
        state.isSessionLoading = false;
      })

      .addMatcher(isAnyOf(searchMediaPool.pending), (state) => {
        state.isMediaPoolLoading = true;
        state.isMediaPoolDescriptionPending = false;
      })
      .addMatcher(isAnyOf(searchMediaPool.fulfilled), (state, action) => {
        state.mediaPool = action.payload;
        state.isMediaPoolLoading = false;
        state.isMediaPoolDescriptionPending = false;
      })
      .addMatcher(isAnyOf(searchMediaPool.rejected), (state, action) => {
        if (action.meta.aborted) {
          return;
        }

        state.isMediaPoolLoading = false;

        if (isDescriptionNotReady(action.payload)) {
          state.isMediaPoolDescriptionPending = true;
          return;
        }

        state.mediaPool = [];
        state.isMediaPoolDescriptionPending = false;
      })

      .addMatcher(isAnyOf(searchGeneratedContent.fulfilled), (state, action) => {
        state.generatedContent = action.payload.content;
        state.generatedContentTableSettings.totalElements = action.payload.totalElements;
      })

      .addMatcher(isAnyOf(changePreview.fulfilled), (state, action) => {
        const updatedItem = action.payload;
        const index = state.generatedContent.findIndex((item) => item.id === updatedItem.id);

        if (index !== -1) {
          state.generatedContent[index] = { ...state.generatedContent[index], preview: updatedItem.preview };
        }
      })

      .addMatcher(isAnyOf(reGenerateStoryContent.fulfilled, setStoryContent.fulfilled), (state, action) => {
        const updatedContent = action.payload.content;
        const contentId = action.meta.arg?.generatedContentId ?? updatedContent?.id;

        if (!updatedContent || !contentId) {
          return;
        }

        state.generatedContent = state.generatedContent.map((bucket) => {
          const contentIndex = bucket.contents.findIndex((content) => content.id === contentId);

          if (contentIndex === -1) {
            return bucket;
          }

          const contents = [...bucket.contents];
          contents[contentIndex] = { ...updatedContent, id: contentId };

          return { ...bucket, contents };
        });
      })

      .addMatcher(isAnyOf(searchSession.fulfilled), (state, action) => {
        state.sessionsData = action.payload.content;
        state.sessionsTableSettings.totalElements = action.payload.totalElements;
        state.isSessionsLoading = false;
      })
      .addMatcher(isAnyOf(searchSession.pending), (state) => {
        state.isSessionsLoading = true;
      })
      .addMatcher(isAnyOf(searchSession.rejected), (state) => {
        state.sessionsData = [];
        state.isSessionsLoading = false;
      })

      .addMatcher(isAnyOf(sendStory.fulfilled), (state) => {
        state.isStorySending = false;
      })
      .addMatcher(isAnyOf(sendStory.pending), (state) => {
        state.isStorySending = true;
      })
      .addMatcher(isAnyOf(sendStory.rejected), (state) => {
        state.isStorySending = false;
      })

      .addMatcher(isAnyOf(clearGeneratedContent.pending), (state) => {
        state.isGeneratedContentClearing = true;
      })
      .addMatcher(isAnyOf(clearGeneratedContent.fulfilled), (state) => {
        state.generatedContent = [];
        state.generatedContentTableSettings.page = initialState.generatedContentTableSettings.page;
        state.generatedContentTableSettings.totalElements = initialState.generatedContentTableSettings.totalElements;
        state.isGeneratedContentClearing = false;
      })
      .addMatcher(isAnyOf(clearGeneratedContent.rejected), (state) => {
        state.isGeneratedContentClearing = false;
      })

      .addMatcher(isAnyOf(getUsedMedia.fulfilled), (state, action) => {
        state.usedMedia = action.payload;
        state.isUsedMediaLoading = false;
      })
      .addMatcher(isAnyOf(getUsedMedia.pending), (state) => {
        state.isUsedMediaLoading = true;
      })
      .addMatcher(isAnyOf(getUsedMedia.rejected), (state) => {
        state.usedMedia = [];
        state.isUsedMediaLoading = false;
      });
  },
});

export const {
  resetStory,
  clearBeforeInit,
  setSelectedMedia,
  setSelectedMediaActiveId,
  setMediaPoolSourceMode,
  setMediaPoolSearchQuery,
  setMediaPoolDescriptionPending,
  setUsedMedia,
  clearMediaPool,
  setSessionId,
  selectSessionFromHistory,
  changeGeneratedContentTableSettings,
  changeSessionsTableSettings,
  changeSelectedMessage,
  changeIsSessionLoading,
} = storySlice.actions;

export default storySlice.reducer;
